/**
 * Base API Service
 * 
 * This file provides a base class for API services that can work with both
 * mock data (for development) and real API calls (for production).
 */

import { apiClient, ApiResponse, handleApiError } from './client';
import { env } from '../../config/env';

/**
 * Base API Service Class
 * Provides common methods for API services
 */
export class BaseApiService {
  protected baseUrl: string;
  protected useMock: boolean;

  constructor(endpoint: string, useMock: boolean = false) {
    this.baseUrl = `/api/${endpoint}`;
    this.useMock = useMock || env.environment === 'development';
  }

  /**
   * Make GET request
   */
  protected async get<T>(url: string, config?: any): Promise<T> {
    if (this.useMock) {
      // In development, return mock data
      // This will be handled by individual service files
      throw new Error('Mock data should be handled by individual service files');
    }

    try {
      const response = await apiClient.get<ApiResponse<T>>(`${this.baseUrl}${url}`, config);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch data');
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Make POST request
   */
  protected async post<T>(url: string, data?: any, config?: any): Promise<T> {
    if (this.useMock) {
      throw new Error('Mock data should be handled by individual service files');
    }

    try {
      const response = await apiClient.post<ApiResponse<T>>(`${this.baseUrl}${url}`, data, config);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create resource');
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Make PUT request
   */
  protected async put<T>(url: string, data?: any, config?: any): Promise<T> {
    if (this.useMock) {
      throw new Error('Mock data should be handled by individual service files');
    }

    try {
      const response = await apiClient.put<ApiResponse<T>>(`${this.baseUrl}${url}`, data, config);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update resource');
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Make PATCH request
   */
  protected async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    if (this.useMock) {
      throw new Error('Mock data should be handled by individual service files');
    }

    try {
      const response = await apiClient.patch<ApiResponse<T>>(`${this.baseUrl}${url}`, data, config);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update resource');
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Make DELETE request
   */
  protected async delete<T>(url: string, config?: any): Promise<T> {
    if (this.useMock) {
      throw new Error('Mock data should be handled by individual service files');
    }

    try {
      const response = await apiClient.delete<ApiResponse<T>>(`${this.baseUrl}${url}`, config);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to delete resource');
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

/**
 * Helper function to switch between mock and real API
 * - REACT_APP_USE_MOCK_API=false → استخدم API الحقيقي دائماً
 * - REACT_APP_USE_MOCK_API=true → استخدم Mock دائماً
 * - غير معرّف: في production نستخدم Real، في development نستخدم Mock
 */
export const shouldUseMock = (): boolean => {
  if (process.env.REACT_APP_USE_MOCK_API === 'false') return false;
  if (process.env.REACT_APP_USE_MOCK_API === 'true') return true;
  return env.environment !== 'production';
};

/** للعرض في الواجهة — هل البيانات من Mock أم من السيرفر */
export const getApiDataSource = (): 'mock' | 'real' =>
  shouldUseMock() ? 'mock' : 'real';

/** Unwrap `{ data: T }` envelope (common on this backend). */
export function unwrap<T>(data: unknown): T {
  const d = data as { data?: T };
  return d?.data != null ? d.data : (data as T);
}

/**
 * Extract list items from varied API shapes, e.g.:
 * `{ data: [...] }`, `{ data: { banners: [...] } }`, `{ banners: [...] }`
 */
export function extractListFromResponse(
  raw: unknown,
  entityKeys: string[]
): Record<string, unknown>[] {
  const tryExtract = (value: unknown): Record<string, unknown>[] | null => {
    if (Array.isArray(value)) {
      return value as Record<string, unknown>[];
    }
    if (!value || typeof value !== 'object') {
      return null;
    }
    const obj = value as Record<string, unknown>;
    for (const key of entityKeys) {
      const nested = obj[key];
      if (Array.isArray(nested)) {
        return nested as Record<string, unknown>[];
      }
    }
    const inner = obj.data;
    if (Array.isArray(inner)) {
      return inner as Record<string, unknown>[];
    }
    if (inner && typeof inner === 'object') {
      return tryExtract(inner);
    }
    return null;
  };

  return tryExtract(unwrap(raw)) ?? tryExtract(raw) ?? [];
}

/** Omit empty values and literal "all" so backends do not treat them as UUIDs/enums. */
export function cleanListQueryParams(
  params: Record<string, string | number | boolean | undefined | null>
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => {
      if (v === undefined || v === null || v === '') return false;
      if (typeof v === 'string' && v.trim().toLowerCase() === 'all') return false;
      return true;
    })
  ) as Record<string, string | number>;
}

